<template>
  <div
    class="modal fade show d-block"
    tabindex="-1"
    role="dialog"
    style="background: rgba(0, 0, 0, 0.8); z-index: 9999;"
  >
    <div class="modal-dialog" role="document">
      <div class="modal-content bg-dark text-light retention-add-to-group-modal">
        <div class="modal-header border-bottom border-secondary">
          <h5 class="modal-title">
            <i class="fas fa-layer-group mr-2"></i>
            Add "{{ sourceMessage.name || '(unnamed)' }}" to a group
          </h5>
          <button type="button" class="close text-light" :disabled="submitting" @click="close">
            <span aria-hidden="true">&times;</span>
          </button>
        </div>

        <div class="modal-body">
          <p class="source-line">
            This message's language:
            <span class="source-tag">{{ sourceLanguageLabel }} — {{ sourceMessage.language }}</span>
          </p>
          <p class="intro">
            Pick the group to add it to. A group can hold only one message per locale, so groups that
            already have a <strong>{{ sourceMessage.language }}</strong> message are disabled.
          </p>

          <div v-if="eligibleGroupsView.length === 0" class="muted empty-note">
            No groups exist yet. Create one by duplicating a message to other languages.
          </div>

          <div v-else class="group-list">
            <label
              v-for="g in eligibleGroupsView"
              :key="g.groupId"
              class="group-row"
              :class="{ disabled: !g.eligible, selected: selectedGroupId === g.groupId }"
            >
              <input
                type="radio"
                name="add-to-group"
                :value="g.groupId"
                :disabled="!g.eligible"
                :checked="selectedGroupId === g.groupId"
                @change="selectedGroupId = g.groupId"
              />
              <span class="group-info">
                <span class="group-row-name">{{ g.name }}</span>
                <span class="group-locales">
                  <span v-for="code in g.langs" :key="code" class="locale-chip">{{ code }}</span>
                </span>
              </span>
              <span v-if="!g.eligible" class="reason">already has {{ sourceMessage.language }}</span>
              <span v-else class="member-count">{{ g.memberCount }}</span>
            </label>
          </div>
        </div>

        <div class="modal-footer border-top border-secondary">
          <button type="button" class="btn btn-secondary" :disabled="submitting" @click="close">
            Cancel
          </button>
          <button
            type="button"
            class="btn btn-primary"
            :disabled="!selectedGroupId || submitting"
            @click="submit"
          >
            {{ submitting ? 'Adding…' : 'Add to group' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { localeLabelByCode } from '../../constants/retentionLocales';

export default {
  name: 'RetentionAddToGroupModal',
  props: {
    sourceMessage: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      selectedGroupId: null,
      submitting: false
    };
  },
  computed: {
    sourceLanguageLabel() {
      return localeLabelByCode(this.sourceMessage.language);
    },
    groups() {
      return this.$store.getters['retention/groups'] || [];
    },
    messagesByGroupId() {
      return this.$store.getters['retention/messagesByGroupId'] || {};
    },
    eligibleGroupsView() {
      const sourceLang = this.sourceMessage.language;
      return this.groups
        .map((g) => {
          const members = this.messagesByGroupId[g.groupId] || [];
          const langs = members.map((m) => m.language).filter(Boolean);
          return {
            groupId: g.groupId,
            name: g.name || '(unnamed group)',
            memberCount: members.length,
            langs,
            members,
            eligible: !langs.includes(sourceLang)
          };
        })
        .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    }
  },
  methods: {
    async submit() {
      if (!this.selectedGroupId || this.submitting) return;
      const group = this.eligibleGroupsView.find((g) => g.groupId === this.selectedGroupId);
      if (!group || !group.eligible) return;
      // Backend resolves the group from any member; pick one with a language.
      const target = group.members.find((m) => m.language);
      if (!target) return;
      this.submitting = true;
      try {
        await this.$store.dispatch('retention/addMessageToGroup', {
          messageId: this.sourceMessage.id,
          targetMessageId: target.id
        });
        this.$emit('close');
      } catch (err) {
        console.error(err);
        this.submitting = false;
      }
    },
    close() {
      if (this.submitting) return;
      this.$emit('close');
    }
  }
};
</script>

<style scoped>
.retention-add-to-group-modal .modal-header { padding: 1rem 1.25rem; }
.retention-add-to-group-modal .modal-body { padding: 1.25rem; }
.retention-add-to-group-modal .modal-footer { padding: 1rem 1.25rem; }

.retention-add-to-group-modal .modal-header .modal-title {
  font-size: 1.05rem;
  color: #fff;
}

.retention-add-to-group-modal .source-line {
  font-size: 0.82rem;
  color: rgba(255, 255, 255, 0.65);
  margin-bottom: 0.5rem;
}

.retention-add-to-group-modal .source-tag {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 999px;
  background: rgba(3, 179, 218, 0.15);
  color: #6dd0ea;
  font-size: 0.78rem;
  margin-left: 0.4rem;
}

.retention-add-to-group-modal .intro {
  color: rgba(255, 255, 255, 0.75);
  font-size: 0.85rem;
  margin-bottom: 1rem;
}

.retention-add-to-group-modal .muted { color: rgba(255, 255, 255, 0.4); }
.retention-add-to-group-modal .empty-note {
  padding: 1rem;
  text-align: center;
  background: rgba(255, 255, 255, 0.03);
  border: 1px dashed rgba(255, 255, 255, 0.12);
  border-radius: 6px;
  font-size: 0.85rem;
}

.retention-add-to-group-modal .group-list {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  max-height: 320px;
  overflow-y: auto;
  padding: 0.4rem;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 6px;
}

.retention-add-to-group-modal .group-row {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.5rem 0.6rem;
  border-radius: 6px;
  cursor: pointer;
  margin: 0;
  border: 1px solid transparent;
}

.retention-add-to-group-modal .group-row:hover:not(.disabled) {
  background: rgba(255, 255, 255, 0.05);
}

.retention-add-to-group-modal .group-row.selected {
  background: rgba(3, 179, 218, 0.12);
  border-color: rgba(3, 179, 218, 0.4);
}

.retention-add-to-group-modal .group-row.disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.retention-add-to-group-modal .group-row input[type="radio"] {
  accent-color: #03B3DA;
  margin: 0;
}

.retention-add-to-group-modal .group-info {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  flex: 1;
  min-width: 0;
}

.retention-add-to-group-modal .group-row-name {
  color: rgba(255, 255, 255, 0.92);
  font-weight: 600;
  font-size: 0.88rem;
}

.retention-add-to-group-modal .group-locales {
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem;
}

.retention-add-to-group-modal .locale-chip {
  padding: 1px 6px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.08);
  color: rgba(255, 255, 255, 0.6);
  font-size: 0.7rem;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
}

.retention-add-to-group-modal .reason {
  font-size: 0.74rem;
  color: #ffb84d;
  white-space: nowrap;
}

.retention-add-to-group-modal .member-count {
  font-size: 0.78rem;
  color: rgba(255, 255, 255, 0.45);
  font-variant-numeric: tabular-nums;
}

.retention-add-to-group-modal .close {
  background: none;
  border: none;
  font-size: 1.5rem;
  font-weight: 700;
  line-height: 1;
  opacity: 0.5;
  cursor: pointer;
  color: #fff;
}
.retention-add-to-group-modal .close:hover:not(:disabled) { opacity: 0.85; }
.retention-add-to-group-modal .close:disabled { cursor: not-allowed; }
</style>
